/**
 * ProjectViewer — wired into /p/<id> route after auth.
 *
 * Flow:
 *   1. Fetch project metadata via projectClient.getProject(id) → name, bbox
 *   2. Mint a signed tileset URL via projectClient.mintTilesetUrl(id)
 *   3. Render <Scene tilesetUrl={signedUrl}/>
 *
 * Loading + error states share a simple full-screen layout so the user
 * always sees something meaningful (not a blank canvas).
 */

import { useEffect, useState } from 'react'
import { getProject, mintTilesetUrl, ProjectMetadata } from '../api/projectClient'
import { listAnnotations, ServerAnnotation } from '../api/annotationsClient'
import { listScreenshots, ServerScreenshot } from '../api/screenshotsClient'
import { Scene } from '../viewer/Scene'
import { RealtimeProvider } from '../realtime/RealtimeProvider'

interface ProjectViewerProps {
  projectId: string
}

type State =
  | { kind: 'loading'; message: string }
  | { kind: 'error'; title: string; message: string }
  | {
      kind: 'ready'
      project: ProjectMetadata
      tilesetUrl: string
      initialAnnotations: ServerAnnotation[]
      initialScreenshots: ServerScreenshot[]
    }

export function ProjectViewer({ projectId }: ProjectViewerProps) {
  const [state, setState] = useState<State>({ kind: 'loading', message: 'Projekt wird geladen…' })
  // null = still probing, true = needs capture, false = existing thumbnail loads fine
  const [needsThumbnail, setNeedsThumbnail] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setState({ kind: 'loading', message: 'Projekt wird geladen…' })
        const project = await getProject(projectId)
        if (cancelled) return
        if (!project.hasTileset) {
          setState({
            kind: 'error',
            title: 'Modell noch nicht verfügbar',
            message: 'Dieses Projekt wurde noch nicht vollständig veröffentlicht.',
          })
          return
        }
        setState({ kind: 'loading', message: 'Modell wird vorbereitet…' })
        const [signed, initialAnnotations, initialScreenshots] = await Promise.all([
          mintTilesetUrl(projectId),
          // Failures on side data don't block the viewer — fall back to []
          // and surface the error in the console for debugging.
          listAnnotations(projectId).catch((err) => {
            console.warn('[Annotations] initial fetch failed:', err)
            return [] as ServerAnnotation[]
          }),
          listScreenshots(projectId).catch((err) => {
            console.warn('[Screenshots] initial fetch failed:', err)
            return [] as ServerScreenshot[]
          }),
        ])
        if (cancelled) return
        setState({
          kind: 'ready',
          project,
          tilesetUrl: signed.signedUrl,
          initialAnnotations,
          initialScreenshots,
        })

        // Probe the existing thumbnail. If it's missing or fails to load
        // (e.g. bucket ACL blocks it), Scene will capture & upload a fresh
        // one once the tileset stabilises.
        if (!project.thumbnailUrl) {
          setNeedsThumbnail(true)
        } else {
          const img = new Image()
          img.onload = () => { if (!cancelled) setNeedsThumbnail(false) }
          img.onerror = () => { if (!cancelled) setNeedsThumbnail(true) }
          img.src = project.thumbnailUrl
        }
      } catch (err) {
        if (cancelled) return
        const raw = err instanceof Error ? err.message : String(err)
        // projectClient throws "Access denied for project ..." on 403 — that's
        // the case where the share has expired or was revoked.
        const lower = raw.toLowerCase()
        if (lower.includes('access denied') || lower.includes('403')) {
          setState({
            kind: 'error',
            title: 'Zugriff abgelaufen',
            message:
              'Dieses Modell wurde Dir zuvor freigegeben, der Link ist jedoch abgelaufen. ' +
              'Bitte den Absender um eine neue Freigabe.',
          })
          return
        }
        if (lower.includes('not found') || lower.includes('404')) {
          setState({
            kind: 'error',
            title: 'Projekt nicht gefunden',
            message: 'Dieses Projekt existiert nicht oder wurde entfernt.',
          })
          return
        }
        setState({ kind: 'error', title: 'Projekt konnte nicht geöffnet werden', message: raw })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [projectId])

  if (state.kind === 'loading') {
    return <CenteredMessage>{state.message}</CenteredMessage>
  }
  if (state.kind === 'error') {
    return (
      <CenteredMessage tone="error">
        <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>{state.title}</div>
        <div style={{ fontSize: '0.9rem', opacity: 0.7 }}>{state.message}</div>
        <div style={{ marginTop: '1rem' }}>
          <a
            href="/"
            style={{
              color: '#9ec5ff',
              textDecoration: 'none',
              fontSize: '0.85rem',
              borderBottom: '1px solid rgba(158,197,255,0.3)',
            }}
          >
            ← Zurück zur Projektliste
          </a>
        </div>
      </CenteredMessage>
    )
  }
  return (
    <RealtimeProvider projectId={state.project.id}>
      <Scene
        projectId={state.project.id}
        tilesetUrl={state.tilesetUrl}
        initialAnnotations={state.initialAnnotations}
        initialScreenshots={state.initialScreenshots}
        captureThumbnailFor={needsThumbnail ? state.project.id : undefined}
      />
    </RealtimeProvider>
  )
}

function CenteredMessage({
  children,
  tone = 'info',
}: {
  children: React.ReactNode
  tone?: 'info' | 'error'
}) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        textAlign: 'center',
        color: tone === 'error' ? '#f88' : '#ddd',
      }}
    >
      <div>{children}</div>
    </div>
  )
}
